import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useSEO, useJSONLD } from '@/hooks/useSEO';
import { platformStats } from '@/mocks/faqs';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useSEO({
    title: '关于我们 - 91.cn com官方入口·正版内容平台导航',
    description: '了解91.cn com平台的品牌故事、发展历程、信誉资质与联系方式。91.cn com致力于为用户提供高质量的数字内容服务，以用户需求为导向，持续提供优质内容体验。',
    keywords: '91.cn com关于我们,91.cn com品牌介绍,91.cn com联系方式,91.cn com信誉',
    canonicalUrl: 'https://91cn.com/about',
  });

  useJSONLD({
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 91.cn com',
    description: '了解91.cn com平台的品牌故事、发展历程、信誉资质与联系方式',
    url: 'https://91cn.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '91.cn com 官方入口',
      url: 'https://91cn.com',
    },
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setFormStatus('success');
      setFormError('');
      form.reset();
      return;
    }

    setSubmitting(true);
    setFormError('');

    try {
      const formData = new FormData(form);
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });
      const response = await fetch('https://readdy.ai/api/form/d9pj4nli2vhsdh2d35og', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || '提交失败，请稍后再试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络连接失败，请检查网络后重试');
      setFormStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb items={[
          { label: '首页', path: '/' },
          { label: '关于我们' },
        ]} />

        {/* Hero */}
        <section className="relative w-full h-[350px] md:h-[420px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20professional%20office%20interior%20with%20warm%20amber%20lighting%2C%20minimalist%20design%2C%20clean%20aesthetic%20with%20abstract%20geometric%20elements%2C%20cream%20walls%20with%20subtle%20texture%2C%20elegant%20contemporary%20workplace%20atmosphere%2C%20soft%20natural%20light%20from%20windows%2C%20high%20quality%20editorial%20photography&width=1800&height=600&seq=about-hero-91cn&orientation=landscape"
            alt="关于91.cn com"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/45"></div>
          <div className="relative z-10 w-full max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-white font-heading mb-4">关于 91.cn com</h1>
            <p className="text-base md:text-lg text-white/80 max-w-xl mx-auto">致力于为用户提供优质、安全、便捷的数字内容服务</p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-6">品牌介绍</h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  <p>
                    91.cn com是一个综合性网络内容平台，自创立以来始终坚持<strong>以用户需求为导向</strong>的发展理念，致力于为用户提供丰富、优质、安全的内容服务。平台汇聚了海量的数字内容资源，涵盖多个领域和类别，满足不同用户群体的多样化需求。
                  </p>
                  <p>
                    我们深知内容质量是平台的生命线。91.cn com建立了严格的内容审核机制和质量管理体系，确保平台上的所有内容<strong>合规、健康、优质</strong>，让每一位用户都能放心使用。
                  </p>
                  <p>
                    在技术层面，91.cn com持续投入研发资源，不断优化平台的<strong>智能推荐算法</strong>、搜索功能和用户体验，致力于为每一位用户提供个性化、精准化的内容服务。
                  </p>
                </div>
              </div>
              <div className="w-full h-80 md:h-96 rounded-lg overflow-hidden bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20digital%20content%20platform%20brand%20visualization%2C%20warm%20orange%20and%20cream%20color%20scheme%2C%20abstract%20flowing%20data%20visualization%2C%20clean%20minimalist%20design%20with%20geometric%20shapes%2C%20soft%20ambient%20lighting%2C%20professional%20corporate%20aesthetic%2C%20high%20quality%20editorial%20illustration&width=800&height=600&seq=about-brand-91cn&orientation=landscape"
                  alt="品牌形象展示"
                  className="w-full h-full object-cover"
                  title="91.cn com 品牌形象"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full py-12 md:py-16 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {platformStats.map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-lg bg-background-50">
                  <i className={`${stat.icon} text-2xl text-primary-500 mb-3 block`}></i>
                  <div className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">{stat.value}</div>
                  <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation & Values */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-10 text-center">信誉资质与核心价值</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: 'ri-award-line',
                  title: '合规运营',
                  desc: '严格遵守国家相关法律法规，所有内容和运营环节均符合监管要求，确保平台合法合规运营。',
                },
                {
                  icon: 'ri-shield-star-line',
                  title: '安全保障',
                  desc: '多层安全防护体系，通过数据加密、访问控制等技术手段，全方位保护用户信息和数据安全。',
                },
                {
                  icon: 'ri-service-line',
                  title: '用户至上',
                  desc: '始终将用户体验放在首位，持续优化产品功能和服务质量，倾听用户反馈并及时响应。',
                },
                {
                  icon: 'ri-lightbulb-line',
                  title: '创新驱动',
                  desc: '不断投入技术创新和产品研发，通过智能算法和大数据技术，提供更智能化的内容服务。',
                },
              ].map((item) => (
                <div key={item.title} className="p-6 rounded-lg bg-background-100 text-center">
                  <div className="w-14 h-14 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
                    <i className={`${item.icon} text-2xl text-accent-600`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2 font-heading">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-6">联系我们</h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">
                  如果您有任何问题、建议或合作意向，欢迎通过以下方式与我们联系。我们的客服团队<strong>7×24小时</strong>在线，将尽快为您提供帮助。
                </p>
                <div className="space-y-5">
                  {[
                    { icon: 'ri-mail-line', label: '电子邮箱', value: 'service@91cn.com' },
                    { icon: 'ri-phone-line', label: '客服热线', value: '400-XXX-XXXX' },
                    { icon: 'ri-map-pin-line', label: '公司地址', value: '中国北京市朝阳区' },
                    { icon: 'ri-time-line', label: '服务时间', value: '7×24小时全年无休' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-lg text-primary-600`}></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-900">{item.label}</div>
                        <div className="text-sm text-foreground-500">{item.value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="bg-background-50 rounded-lg p-6 md:p-8">
                  <h3 className="text-lg font-semibold text-foreground-950 font-heading mb-6">发送消息</h3>
                  {formStatus === 'success' ? (
                    <div className="text-center py-10">
                      <i className="ri-checkbox-circle-line text-5xl text-accent-500 mb-4 block"></i>
                      <p className="text-foreground-900 font-medium text-lg mb-1">消息已发送！</p>
                      <p className="text-sm text-foreground-500">感谢您的留言，我们会尽快回复您。</p>
                      <button
                        onClick={() => setFormStatus('idle')}
                        className="mt-6 px-6 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        发送新消息
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-5">
                      <div className="spam-field-container">
                        <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground-700 mb-1.5">姓名 *</label>
                          <input
                            type="text"
                            name="name"
                            required
                            className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱 *</label>
                          <input
                            type="email"
                            name="email"
                            required
                            className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">主题 *</label>
                        <input
                          type="text"
                          name="subject"
                          required
                          className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入消息主题"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">消息内容 *</label>
                        <textarea
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                          placeholder="请输入您的消息（最多500字）"
                        ></textarea>
                        <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                      </div>
                      {formStatus === 'error' && (
                        <div className="text-sm text-red-600 bg-red-50 px-4 py-2.5 rounded-lg">{formError}</div>
                      )}
                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full px-6 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 cursor-pointer whitespace-nowrap"
                      >
                        {submitting ? '发送中...' : '发送消息'}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}